import { FC } from 'react';

import {
  GiftCouponRedeemStatus,
  GiftCouponSummary,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { formatPrice } from '@shared/utils/formatPrice';

import {
  fmt,
  fmtPhone,
  fmtTime,
  isCouponCancellable,
  isCouponDisposable,
  redeemStatusColor,
  redeemStatusLabel,
  shareStatusColor,
  shareStatusLabel,
} from './constants';
import { GiftItemEmbed } from './types';

interface CouponRowProps {
  coupon: GiftCouponSummary;
  embed: GiftItemEmbed | undefined;
  canCancel: boolean;
  hasCancellableCoupons: boolean;
  disposeMode: boolean;
  isSelected: boolean;
  isDisposeSelected: boolean;
  expandedShareHistory: Set<string>;
  isPromoting: boolean;
  toggleSelect: (id: string) => void;
  toggleDisposeSelect: (id: string) => void;
  toggleShareHistory: (id: string) => void;
  handlePromoteToReissued: (couponId: string) => Promise<void>;
  onIssueRefund?: (couponId: string) => void;
  onIssueRenewal?: (couponId: string) => void;
  onCancelIssue?: (couponId: string) => void;
  isIssuing?: boolean;
}

export const CouponRow: FC<CouponRowProps> = ({
  coupon: c,
  embed,
  canCancel,
  hasCancellableCoupons,
  disposeMode,
  isSelected,
  isDisposeSelected,
  expandedShareHistory,
  isPromoting,
  toggleSelect,
  toggleDisposeSelect,
  toggleShareHistory,
  handlePromoteToReissued,
  onIssueRefund,
  onIssueRenewal,
  onCancelIssue,
  isIssuing,
}) => {
  const cancellable = isCouponCancellable(c);
  const disposable = isCouponDisposable(c);
  const isCanceled = c.redeemStatus === GiftCouponRedeemStatus.Canceled;
  const isDisposed = c.redeemStatus === GiftCouponRedeemStatus.Disposed;
  const isInactive = isCanceled || isDisposed;

  return (
    <tr
      className={`${
        isInactive
          ? 'bg-gray-50 opacity-60'
          : isDisposeSelected
          ? 'bg-slate-100'
          : isSelected
          ? 'bg-red-50'
          : c.isAdditional || c.isReissued
          ? 'bg-amber-50/40 hover:bg-amber-50'
          : 'hover:bg-gray-50'
      }`}
    >
      {canCancel && hasCancellableCoupons && !disposeMode && (
        <td className="whitespace-nowrap px-3 py-1.5">
          {cancellable ? (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => toggleSelect(c._id)}
              className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
            />
          ) : (
            <span className="text-gray-300">-</span>
          )}
        </td>
      )}
      {disposeMode && (
        <td className="whitespace-nowrap px-3 py-1.5">
          {disposable ? (
            <input
              type="checkbox"
              checked={isDisposeSelected}
              onChange={() => toggleDisposeSelect(c._id)}
              className="h-4 w-4 rounded border-slate-300 text-slate-600 focus:ring-slate-500"
            />
          ) : (
            <span className="text-gray-300">-</span>
          )}
        </td>
      )}
      <td className="whitespace-nowrap px-3 py-1.5 text-gray-400">{c.seq ?? ''}</td>
      <td className="whitespace-nowrap px-3 py-1.5">
        {c.isReissued ? (
          <span className="inline-flex rounded-full bg-teal-100 px-1.5 py-0.5 text-[10px] font-bold leading-tight text-teal-700">
            재발급
          </span>
        ) : c.isAdditional ? (
          <div className="flex items-center gap-1">
            <span className="inline-flex rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold leading-tight text-amber-700">
              재발행
            </span>
            {c.redeemStatus === GiftCouponRedeemStatus.Available && (
              <button
                onClick={() => handlePromoteToReissued(c._id)}
                disabled={isPromoting}
                className="inline-flex rounded bg-teal-50 px-1 py-0.5 text-[9px] font-semibold text-teal-600 hover:bg-teal-100 disabled:opacity-50"
                title="재발행 → 재발급 승격 (isAdditional → isReissued)"
              >
                승격
              </button>
            )}
          </div>
        ) : (
          <span className="text-gray-300">-</span>
        )}
      </td>
      <td className="max-w-[120px] whitespace-nowrap px-3 py-1.5">
        {embed ? (
          <span className="block truncate font-medium text-gray-800" title={embed.name}>
            {embed.name}
          </span>
        ) : (
          <span className="text-gray-300">-</span>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5 text-gray-500">
        {embed ? `${formatPrice(embed.priceRetail)}원` : '-'}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5 font-mono font-medium text-gray-800">
        {c.code}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5">
        <span
          className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-tight ${
            redeemStatusColor[c.redeemStatus]
          }`}
        >
          {redeemStatusLabel[c.redeemStatus]}
        </span>
        {isDisposed && c.disposalReason && (
          <p
            className="mt-0.5 max-w-[100px] truncate text-[9px] text-slate-400"
            title={c.disposalReason}
          >
            {c.disposalReason}
          </p>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5">
        <span
          className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-tight ${
            shareStatusColor[c.shareStatus]
          }`}
        >
          {shareStatusLabel[c.shareStatus]}
        </span>
      </td>
      <td className="whitespace-nowrap px-3 py-1.5 text-gray-500">
        {fmt(c.purchasedAt)}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5">
        {c.redeemStatus === GiftCouponRedeemStatus.Delivered &&
        c.gifticon?.deliveredAt ? (
          <span className="font-medium text-green-600">
            {fmt(c.gifticon.deliveredAt)}
          </span>
        ) : (
          <span className="text-gray-500">{fmt(c.expiresAt)}</span>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5 text-gray-500">
        {c.sharedAt ? fmt(c.sharedAt) : '-'}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5 text-gray-500">
        {c.viewedAt ? fmt(c.viewedAt) : '-'}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5 text-gray-700">
        {c.recipient ? (
          <span>
            {c.recipient.name}{' '}
            <span className="text-gray-400">{fmtPhone(c.recipient.phoneNumber)}</span>
          </span>
        ) : (
          <span className="text-gray-300">-</span>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5 text-gray-500">
        {c.recipient ? fmtTime(c.recipient.requestedAt) : '-'}
      </td>
      <td className="max-w-[150px] px-3 py-1.5">
        {c.memo ? (
          <span className="block truncate text-gray-700" title={c.memo}>
            {c.memo}
          </span>
        ) : (
          <span className="text-gray-300">-</span>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5 text-gray-500">
        {c.confirmedAt ? fmtTime(c.confirmedAt) : '-'}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5">
        {c.additionalRecipientPhone ? (
          <div>
            <span className="text-xs text-amber-700">
              {fmtPhone(c.additionalRecipientPhone)}
            </span>
            <p className="text-[9px] text-amber-500">확인번호</p>
          </div>
        ) : (
          <span className="text-gray-300">-</span>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5">
        {c.shareHistory && c.shareHistory.length > 0 ? (
          <div className="relative">
            <button
              onClick={() => toggleShareHistory(c._id)}
              className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-700 hover:bg-indigo-100"
            >
              {c.shareHistory.length}회
              <svg
                className={`h-3 w-3 transition-transform ${
                  expandedShareHistory.has(c._id) ? 'rotate-180' : ''
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            {expandedShareHistory.has(c._id) && (
              <div className="absolute left-0 top-full z-10 mt-1 min-w-[200px] rounded-lg border border-gray-200 bg-white p-2 shadow-lg">
                <div className="mb-1 text-[10px] font-semibold text-gray-500">
                  공유 이력
                </div>
                {c.shareHistory.map((h, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 border-t border-gray-100 py-1 text-[10px]"
                  >
                    <span className="text-gray-400">{i + 1}.</span>
                    <span className="text-indigo-600">공유 {fmtTime(h.sharedAt)}</span>
                    {h.unsharedAt && (
                      <span className="text-red-500">→ 해제 {fmtTime(h.unsharedAt)}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <span className="text-gray-300">-</span>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5">
        {c.issued?.type === 'refund' ? (
          <div className="flex items-center gap-1">
            <span className="inline-flex rounded-full bg-orange-100 px-1.5 py-0.5 text-[9px] font-semibold text-orange-700">
              환불 ·{' '}
              {c.refund?.refundedAt
                ? '완료'
                : c.issued.requestedAt
                ? '신청완료'
                : '링크발행'}
            </span>
            {c.refund?.amount && (
              <span className="text-[9px] text-red-600">
                {c.refund.amount.toLocaleString()}원
              </span>
            )}
            {onCancelIssue && !c.issued.requestedAt && (
              <button
                onClick={() => onCancelIssue(c._id)}
                disabled={isIssuing}
                className="text-[9px] text-red-500 hover:underline disabled:opacity-50"
              >
                취소
              </button>
            )}
          </div>
        ) : c.issued?.type === 'renewal' ? (
          <div className="flex items-center gap-1">
            <span className="inline-flex rounded-full bg-violet-100 px-1.5 py-0.5 text-[9px] font-semibold text-violet-700">
              연장 ·{' '}
              {c.isRenewed ? '완료' : c.issued.requestedAt ? '신청완료' : '링크발행'}
            </span>
            {onCancelIssue && !c.issued.requestedAt && !c.isRenewed && (
              <button
                onClick={() => onCancelIssue(c._id)}
                disabled={isIssuing}
                className="text-[9px] text-red-500 hover:underline disabled:opacity-50"
              >
                취소
              </button>
            )}
          </div>
        ) : (onIssueRefund || onIssueRenewal) &&
          !c.refund &&
          c.redeemStatus !== GiftCouponRedeemStatus.Canceled &&
          c.redeemStatus !== GiftCouponRedeemStatus.Disposed &&
          c.redeemStatus !== GiftCouponRedeemStatus.Refunded ? (
          <div className="flex items-center gap-1">
            {onIssueRefund && (
              <button
                onClick={() => onIssueRefund(c._id)}
                disabled={isIssuing}
                className="rounded bg-orange-50 px-2 py-0.5 text-[10px] font-semibold text-orange-700 hover:bg-orange-100 disabled:opacity-50"
              >
                환불
              </button>
            )}
            {onIssueRefund &&
              onIssueRenewal &&
              c.redeemStatus === GiftCouponRedeemStatus.Delivered &&
              !c.isRenewed &&
              !c.isRenewal && <span className="text-gray-300">|</span>}
            {onIssueRenewal &&
              c.redeemStatus === GiftCouponRedeemStatus.Delivered &&
              !c.isRenewed &&
              !c.isRenewal && (
                <button
                  onClick={() => onIssueRenewal(c._id)}
                  disabled={isIssuing}
                  className="rounded bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-700 hover:bg-violet-100 disabled:opacity-50"
                >
                  연장
                </button>
              )}
          </div>
        ) : (
          <span className="text-gray-300">-</span>
        )}
      </td>
    </tr>
  );
};
